import React from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Separator } from '@vaiu/ui/separator';
import { useShoppingCart } from '@/hooks/useShoppingCart';
import { CurrencyDisplay } from '@vaiu/ui';

interface CartSummaryProps {
  onCheckout?: () => void;
  showActions?: boolean;
}

export const CartSummary: React.FC<CartSummaryProps> = ({ 
  onCheckout, 
  showActions = true 
}) => {
  const { 
    items, 
    getTotalAmount, 
    updateQuantity, 
    removeItem, 
    currency,
    merchantName 
  } = useShoppingCart();

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-gray-500">Your cart is empty</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const total = getTotalAmount();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Order Summary</CardTitle>
        {merchantName && (
          <p className="text-sm text-gray-600">{merchantName}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((item) => (
          <div key={item.productId} className="flex items-start space-x-3">
            {item.image && (
              <img
                src={item.image}
                alt={item.name}
                className="w-16 h-16 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <h4 className="font-medium">{item.name}</h4>
              {item.category && (
                <p className="text-xs text-gray-500">{item.category}</p>
              )}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-2">
                  {showActions ? (
                    <>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-6 w-6"
                        onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-6 w-6"
                        onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <CurrencyDisplay
                    amount={item.price * item.quantity}
                    currency={item.currency}
                    className="font-medium"
                  />
                  {showActions && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => removeItem(item.productId)}
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        <Separator />
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Subtotal</span>
            <CurrencyDisplay amount={total} currency={currency} currencyService={currencyService} />
          </div>
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <CurrencyDisplay amount={total} currency={currency} currencyService={currencyService} />
          </div>
        </div>

        {onCheckout && showActions && (
          <Button 
            onClick={onCheckout} 
            className="w-full"
            size="lg"
          >
            Proceed to Checkout
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
